import { Box } from "@mui/material";

import { useIsMobile } from "../AppLayout/useResponsive";
import { BottomSheet } from "./BottomSheet";
import { Dialog, type DialogProps } from "./Dialog";
import { DialogHeader } from "./DialogHeader";

export type ResponsiveDialogMobileVariant = "bottomSheet" | "fullScreen";

export interface ResponsiveDialogProps extends DialogProps {
  /**
   * Width of the dialog in pixels (desktop only, ignored on mobile)
   * @default 400
   */
  width?: number;
  /** When false, prevents closing via swipe and backdrop click on mobile. Defaults to true. */
  dismissible?: boolean;
  /** Custom close handler. Called when X button is clicked. Defaults to modalState.closeModal. */
  onClose?: () => void;
  /**
   * How to render on mobile.
   * - `bottomSheet` (default): a swipeable sheet up to 80% of viewport height
   * - `fullScreen`: a fullscreen dialog with the same header / body / footer chrome as desktop
   */
  mobileVariant?: ResponsiveDialogMobileVariant;
}

/**
 * Responsive dialog component that adapts to screen size.
 *
 * - Desktop (>= md): Renders as centered Dialog
 * - Mobile (< md): Renders as bottom sheet by default, or as a fullscreen Dialog when
 *   `mobileVariant="fullScreen"`. Use fullscreen for tall forms where a partial-height
 *   sheet would force inner scroll on top of an already-scrollable body.
 *
 * Features:
 * - Automatic responsive behavior
 * - Unified API for both variants
 * - Consistent header, content, and footer structure
 *
 * @example
 * ```tsx
 * <ResponsiveDialog
 *   modalState={modalState}
 *   title="Confirm action"
 *   footerContent={<DialogFooter ... />}
 * >
 *   <Text>Are you sure?</Text>
 * </ResponsiveDialog>
 * ```
 */
export function ResponsiveDialog(props: ResponsiveDialogProps) {
  const {
    children,
    modalState,
    title,
    showCloseButton = true,
    headerContent,
    footerContent,
    sx,
    dismissible,
    onClose,
    mobileVariant = "bottomSheet",
    ...dialogProps
  } = props;

  const isMobile = useIsMobile();
  const handleClose = onClose ?? modalState.closeModal;

  if (isMobile && mobileVariant === "bottomSheet") {
    return (
      <BottomSheet modalState={modalState} sx={sx} dismissible={dismissible}>
        {headerContent}
        {!headerContent && title && (
          <DialogHeader showCloseButton={showCloseButton} title={title} onClose={handleClose} />
        )}
        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            overflowY: "auto",
            flex: 1,
          })}
        >
          {children}
        </Box>
        {footerContent && (
          <Box
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.border?.base}`,
            })}
          >
            {footerContent}
          </Box>
        )}
      </BottomSheet>
    );
  }

  return (
    <Dialog
      {...dialogProps}
      modalState={modalState}
      title={title}
      showCloseButton={showCloseButton}
      headerContent={headerContent}
      footerContent={footerContent}
      sx={sx}
      fullScreen={isMobile && mobileVariant === "fullScreen"}
      onClose={onClose}
    >
      {children}
    </Dialog>
  );
}
